import { Component, EventEmitter, Input, Output } from '@angular/core';

type ShareButtonLayout = 'horizontal' | 'vertical';

@Component({
    selector: 'app-share-button',
    templateUrl: './share-button.component.html',
    styleUrls: ['./share-button.component.scss'],
    standalone: false
})
export class ShareButtonComponent {
  @Input() icon: string = '';
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() layout: ShareButtonLayout = 'horizontal';
  @Input() extraClasses: string | string[] = [];
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled: boolean = false;
  @Input() ariaLabel?: string;
  @Input() externalUrl?: string;
  @Input() patientData?: any;

  @Output() pressed = new EventEmitter<void>();

  onClick(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.disabled) {
      return;
    }

    // If externalUrl is provided, handle opening with Chrome extension detection
    if (this.externalUrl) {
      this.openWithExtensionDetection(this.externalUrl);
      this.pressed.emit();
      return;
    }

    // Otherwise, emit the pressed event for parent component to handle
    this.pressed.emit();
  }

  /**
   * Opens a URL with Chrome extension detection.
   * If the extension is installed, opens via extension.
   * Otherwise, falls back to opening in a new browser tab.
   *
   * HIPAA COMPLIANCE NOTE:
   * - PHI is NOT logged to console
   * - PHI is NOT stored in sessionStorage
   * - PHI is NOT passed via URL query parameters
   * - Data is only transmitted via secure Chrome extension messaging
   *
   * @param url The URL to open
   */
  private openWithExtensionDetection(url: string): void {
    const chrome = (window as any).chrome;

    // Check if Chrome extension API is available
    if (!chrome || !chrome.runtime || !chrome.runtime.sendMessage) {
      // HIPAA: Do not log URL or patient data
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Extension ID for Medical NuageDx Navigation Helper
    const EXTENSION_ID = 'ljnabhknhhbjdhagcmakbnpeoifimnhn';

    // Prepare patient data - extract only necessary fields for secure transmission
    // HIPAA COMPLIANCE: Data is only transmitted via Chrome extension secure messaging
    // Never log, store in browser storage, or pass via URL
    let dataToSend = null;
    if (this.patientData) {
      // Extract only necessary fields to ensure serialization works
      // Note: This data is transmitted ONLY via secure Chrome extension messaging
      dataToSend = {
        patientId: this.patientData.patientId,
        patientIDs: this.patientData.patientIDs,
        caseName: this.patientData.caseName,
        ownerID: this.patientData.ownerID,
        // Include sharing setup if available (contains origin facility, target facility, selected users, etc.)
        sharingSetup: this.patientData.sharingSetup || null
      };

      // HIPAA: Do NOT include sensitive PHI fields in transmitted data
      // Removed: firstname, lastname, dob, gender, placeOfBirth, mrn, ssn, clientname
    }

    // HIPAA COMPLIANCE: Do NOT store PHI in sessionStorage or URL parameters
    // Data is transmitted ONLY via secure Chrome extension messaging

    // Try to send message to extension (silently fallback if it fails)
    try {
      chrome.runtime.sendMessage(
        EXTENSION_ID,
        {
          type: 'OPEN_EXTERNAL',
          url: url,
          patientData: dataToSend
        },
        (response: any) => {
          if (chrome.runtime.lastError) {
            // Extension not accessible - open URL without patient data
            // HIPAA: Never pass PHI via URL parameters
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
          }

          if (response && response.ok) {
            // Extension handled it successfully
          } else {
            // Fallback: open URL without patient data
            // HIPAA: Never pass PHI via URL parameters
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }
      );
    } catch (error) {
      // HIPAA: Do not log error details that might contain PHI
      // Fallback: open URL without patient data
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  get buttonClasses(): Record<string, boolean> {
    const classes: Record<string, boolean> = {
      'share-button': true,
      'share-button-horizontal': this.layout === 'horizontal'
    };

    this.normalizedExtraClasses.forEach(className => {
      classes[className] = true;
    });

    return classes;
  }

  private get normalizedExtraClasses(): string[] {
    if (Array.isArray(this.extraClasses)) {
      return this.extraClasses.filter(Boolean);
    }

    if (typeof this.extraClasses === 'string' && this.extraClasses.trim().length > 0) {
      return this.extraClasses
        .split(' ')
        .map(token => token.trim())
        .filter(Boolean);
    }

    return [];
  }
}
